import React, { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Star, Sparkles } from "lucide-react";
import { fetchOrderBySession } from "../services/api";

export default function SubscriptionSuccess() {
	const { user, refreshUser } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isTopUp = searchParams.get("type") === "topup";
	const topUpCredits = searchParams.get("credits");
	const sessionId = searchParams.get("session_id");

	useEffect(() => {
		if (!user) {
			navigate("/");
			return;
		}

		const finalizePurchase = async () => {
			if (isTopUp && sessionId) {
				try {
					await fetchOrderBySession(sessionId);
				} catch (err) {
					console.error("Failed to confirm top-up on return from Stripe:", err);
				}
			}

			await refreshUser().catch((err) => {
				console.error("Failed to refresh user after subscription success:", err);
			});
		};

		finalizePurchase();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate]);

	if (!user) return null;

	return (
		<div className='min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-black flex items-center justify-center p-4'>
			<div className='max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20 text-center animate-in fade-in zoom-in duration-500'>
				<div className='mb-6 relative inline-block'>
					<div className='absolute inset-0 bg-green-500 blur-xl opacity-50 rounded-full animate-pulse'></div>
					<CheckCircle className='w-20 h-20 text-green-400 relative z-10 mx-auto' />
				</div>

				<h1 className='text-3xl font-bold text-white mb-2'>
					{isTopUp ? "Credits Added!" : "Upgrade Successful!"}
				</h1>
				<p className='text-purple-200 mb-8'>
					{isTopUp
						? "Your credit top-up has been applied to your account."
						: "Welcome to the inner circle. Your premium access is now active."}
				</p>

				<div className='bg-white/5 rounded-xl p-6 mb-8 border border-white/10 space-y-4'>
					{!isTopUp && (
						<div className='flex items-center justify-center gap-2 text-yellow-400'>
							<Star className='w-5 h-5 fill-yellow-400' />
							<span className='font-semibold tracking-wide uppercase text-sm'>
								Premium Status Unlocked
							</span>
							<Star className='w-5 h-5 fill-yellow-400' />
						</div>
					)}

					<div className='flex items-center justify-center gap-3 bg-white/10 rounded-lg py-3 px-4'>
						<Sparkles className='w-5 h-5 text-purple-300' />
						<span className='text-white font-medium'>
							{isTopUp
								? `${topUpCredits || user?.credits || 0} Credits Added`
								: "30 Credits Added"}
						</span>
					</div>

					{isTopUp && (
						<p className='text-white text-sm font-medium'>
							Current balance: {user?.credits ?? 0} credits
						</p>
					)}

					<p className='text-gray-300 text-sm'>
						{isTopUp
							? "Top-up credits expire when your subscription ends. You can view your balance in your profile."
							: "You can now generate up to 30 advanced reports this month."}
					</p>
				</div>

				<button
					onClick={() => navigate("/app")}
					className='w-full group flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-purple-500/25 transform hover:-translate-y-0.5'
				>
					Start Generating Reports
					<ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
				</button>
			</div>
		</div>
	);
}
